import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import {
  ApiResponse,
  UserManagementDataListing,
  UserManagementDataResponse,
  EmailAvailabilityResponse
} from '../../../types/commons';
import { CreateUserRequest, UpdateUserRequest } from '../../../types/user';
import { HttpStatus } from '../../utils/http-status.util';
import { RuntimeConfigService } from '../common/runtime-config.service';
import { normalizeApiErrors } from '../../utils/api-errors.util';
import { resolveApiErrorMessages } from '../../utils/api-error-messages.util';
import { SKIP_GLOBAL_ERROR_REDIRECT } from '../../utils/http-context-tokens.util';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private readonly config = inject(RuntimeConfigService);
  private readonly API_URl = `${this.config.apiUrl()}/user`;

  constructor(private http: HttpClient) {}

  fetchUserList(params?: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    companyId?: string;
    roleName?: string;
    sortBy?: string;
    isDescending?: boolean;
  }): Observable<UserManagementDataListing> {
    return this.http
      .get<UserManagementDataResponse>(`${this.API_URl}/users`, {
        params: { ...params },
        withCredentials: true,
        // See SKIP_GLOBAL_ERROR_REDIRECT's own docblock: this call's failure
        // is classified and toasted by page-load-failure.util.ts (the
        // USER_MGMT_LISTING scope) — the global interceptor's redirect must
        // not win that race.
        context: new HttpContext().set(SKIP_GLOBAL_ERROR_REDIRECT, true),
      })
      .pipe(map((response) => response.data));
  }

  createUser(credentials: CreateUserRequest): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${this.API_URl}/create-user`, credentials, {
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  updateUser(credentials: UpdateUserRequest): Observable<ApiResponse> {
    return this.http
      .put<ApiResponse>(`${this.API_URl}/update-user`, credentials, {
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  deleteUser(credentials: { id: string }): Observable<ApiResponse> {
    return this.http
      .delete<ApiResponse>(`${this.API_URl}/delete-user`, {
        body: credentials,
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  checkEmailAvailability(email: string): Observable<boolean> {
    return this.http
      .post<EmailAvailabilityResponse>(`${this.API_URl}/email-availability`, {email}, {
        withCredentials: true,
      })
      .pipe(
        map(res => !!res?.isEmailTaken),
        catchError(() => of(false))
      );
  }

  private handleError(
    error: HttpErrorResponse
  ): Observable<UserManagementDataResponse> {
    // `errors` is typed `string[]` but some routes answer with a bare string.
    // A raw `.length` read is truthy for a non-empty string, so the envelope
    // would pass through with `errors` still a string and every consumer
    // downstream would index it by character. Normalize before testing it.
    const errors = normalizeApiErrors(error.error?.errors);
    // The UI keys its copy on `errorCode` (see user-management-error-codes.util.ts),
    // so it must survive on BOTH branches below; dropping it would force callers
    // back onto the `errors` prose, which is not a stable contract.
    const errorCode: unknown = error.error?.errorCode;
    if (errors.length > 0) {
      // Pass the body through when it explains itself (this keeps `errorCode`).
      // The body may omit `statusCode`, so fall back to the HTTP status.
      return of({
        ...error.error,
        errors,
        statusCode: error.error?.statusCode ?? error.status,
      });
    }

    // Otherwise fabricate an envelope with the shared resolution — which
    // covers `create-user` / `update-user` / `delete-user` being Admin-or-SuperAdmin-only:
    // a User-role caller gets a 403 with an EMPTY body, so there is nothing
    // above to fall into and a raw transport string would reach the user.
    return of({
      status: false,
      statusCode: error.status || HttpStatus.InternalServerError,
      data: {} as UserManagementDataListing,
      errors: resolveApiErrorMessages(error),
      ...(typeof errorCode === 'string' && errorCode.length > 0 ? { errorCode } : {}),
    });
  }
}
